export const normalizeTradeValues = (trade) => {
    const numericFields = [
      "avg_buy_price",
      "avg_sell_price",
      "total_bought",
      "total_sold",
      "buy_fees",
      "sell_fees",
      "trade_value",
      "price",
      "fees",
    ];
  
    const normalizedTrade = { ...trade };
  
    numericFields.forEach((field) => {
      if (field in normalizedTrade) {
        normalizedTrade[field] = parseFloat(normalizedTrade[field]) || 0; // Default to 0 if NaN
      }
    });
  
    return normalizedTrade;
  };
  
export const formatDateTimeLocal = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Format date string for display (e.g., "10/3/2025")
export const formatLocalDate = (dateString) => {
  if (!dateString) return '';
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};

export const calculateFees = (order) => {
  // Webull fees reference:
  // https://www.webull.com/pricing#top
  const { buy_sell, quantity, price } = order;
  const totalTradeAmount = quantity * price * 100;
  let fees = 0;

  if (buy_sell === "Sell") {
    fees += parseFloat(Math.max(0.00279 * quantity, 0.01).toFixed(2)); // Trading activity fee FINRA
  }
  fees += parseFloat((0.02945 * quantity).toFixed(2)); // Options regulatory fee
  fees += parseFloat((0.025 * quantity).toFixed(2)); // Clearing fee OCC
  fees += parseFloat((0.0046 * quantity).toFixed(2)); // CAT Regulatory Fee

  return fees.toFixed(2);
};

export const parseOptionName = (optionName) => {
  const [symbol, strike, exp] = optionName.split(" ");
  return { symbol, strike, exp };
};

// Currency formatter helper
export const formatCurrency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Helper to format date as "DayName - M/D"
export const formatGroupDate = (dateStr) => {
  // Fix: Always treat dateStr as UTC and avoid timezone offset
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  // Use Date.UTC and setUTCHours(12) to avoid DST issues
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  d.setUTCHours(12, 0, 0, 0); // Force midday UTC to avoid edge cases
  const dayName = d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
  const m = d.getUTCMonth() + 1;
  const dd = d.getUTCDate();
  return `${dayName} - ${m}/${dd}`;
};

// Group array of items by a date field (returns array of {dateKey, items})
export const groupByDate = (items, dateField) => {
  const groups = {};
  items.forEach(item => {
    const d = new Date(item[dateField]);
    // Use getUTCDate() instead of getDate() to avoid timezone issues
    const dateKey = [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, "0"),
      String(d.getUTCDate()).padStart(2, "0"),
    ].join("-");
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(item);
  });
  const sortedKeys = Object.keys(groups).sort();
  return sortedKeys.map(dateKey => ({
    dateKey,
    items: groups[dateKey]
  }));
};

// Calculate summary for trades/orders
export const calculateSummary = (trades) =>
  trades.reduce(
    (acc, trade) => {
      const total_fees = (trade.buy_fees || 0) + (trade.sell_fees || 0);
      const pnl = (trade.total_sell_price || 0) - (trade.total_buy_price || 0) - total_fees;
      acc.count += 1;
      acc.totalTradeValue += Number(trade.total_buy_price) || 0;
      acc.totalFees += total_fees;
      acc.totalPnL += pnl;
      return acc;
    },
    { count: 0, totalTradeValue: 0, totalFees: 0, totalPnL: 0 }
  );

// Returns {start, end} for the current week (Monday to today)
export const getCurrentWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
  const mondayStr = monday.toISOString().slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);
  return { start: mondayStr, end: todayStr };
};

// Returns {start, end} for a week offset (0 = current, -1 = prev, 1 = next)
export const getWeekRange = (offset = 0) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().slice(0, 10),
    end: sunday.toISOString().slice(0, 10),
  };
};

// Returns {start, end} for a quick search (days = 1, 5, 30)
export const getQuickSearchRange = (days) => {
  const today = new Date();
  if (days === 1) {
    const todayStr = today.toISOString().slice(0, 10);
    return { start: todayStr, end: todayStr };
  }
  const start = new Date();
  start.setDate(today.getDate() - days);
  return {
    start: start.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
};